// „Тежка" клетка на работен лист (Разфасовка, Срок, Склад, Партида): плоска,
// фокусируема и участва в стрелковата навигация като всяка друга клетка, а
// antd Select/DatePicker се монтира ЧАК при Enter/Space/F2/Alt+↓ или клик —
// 700 реда × 3 antd контрола иначе бавят страницата.
//
// В отворения редактор:
//   Esc      — затваря и връща фокуса в клетката (хваща се в capture фазата,
//              преди antd — иначе фокусът падаше в <body> и следващата ↓
//              скачаше на първия ред)
//   ←/→      — излизат към съседната клетка (само при `onSideExit`: Select без
//              търсене; в DatePicker стрелките си движат курсора)
//   избор    — редакторът вика `close()` → затваря и връща фокуса
//   blur     — редакторът вика `close(false)` → затваря, фокусът остава където
//              е отишъл (клик другаде, Tab)
//
// Външният вид (рамка, отстъпи) е на листа — `className` на плоската клетка и
// обвивката около компонента; тук е само смяната клетка ↔ редактор.
// `editorHeight` — височината на antd редактора (controlHeight): решетката
// подава височината на ниския си ред, за да не расте редът при отваряне;
// картите на телефон не я подават и редакторът остава като полетата на листа.
import { useCallback, useEffect, useRef, useState } from "react";
import { ConfigProvider } from "antd";
import type { ReactNode } from "react";
import type { GridRowKey } from "./gridNavigation";

type Props = {
  id: string;
  rowKey: GridRowKey;
  col: number;
  className: string;
  title?: string;
  // съдържанието на затворената клетка (текущата стойност)
  children: ReactNode;
  renderEditor: (close: (refocus?: boolean) => void) => ReactNode;
  onSideExit?: (step: -1 | 1) => void;
  editorHeight?: number;
};

export const PickCell = ({
  id,
  rowKey,
  col,
  className,
  title,
  children,
  renderEditor,
  onSideExit,
  editorHeight,
}: Props) => {
  const [editing, setEditing] = useState(false);
  const cellRef = useRef<HTMLDivElement | null>(null);
  // Връщането на фокуса става СЛЕД като клетката отново е в DOM (ефектът по
  // `editing`). `close(false)` не отменя вече поискано връщане — при избор
  // редакторът се демонтира и някои браузъри пускат blur след onChange.
  const refocusRef = useRef(false);
  useEffect(() => {
    if (editing || !refocusRef.current) return;
    refocusRef.current = false;
    cellRef.current?.focus();
  }, [editing]);
  const close = useCallback((refocus = true) => {
    if (refocus) refocusRef.current = true;
    setEditing(false);
  }, []);

  if (editing) {
    return (
      <div
        className="w-full"
        onKeyDownCapture={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          close(true);
        }}
        onKeyDown={(event) => {
          if (!onSideExit) return;
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
          event.preventDefault();
          event.stopPropagation();
          close(false);
          onSideExit(event.key === "ArrowLeft" ? -1 : 1);
        }}
      >
        {editorHeight ? (
          <ConfigProvider theme={{ token: { controlHeight: editorHeight } }}>{renderEditor(close)}</ConfigProvider>
        ) : (
          renderEditor(close)
        )}
      </div>
    );
  }

  return (
    <div
      ref={cellRef}
      id={id}
      tabIndex={0}
      role="button"
      title={title}
      data-rk={rowKey}
      data-c={col}
      onClick={() => setEditing(true)}
      onKeyDown={(event) => {
        if (event.ctrlKey || event.metaKey) return; // Ctrl+Enter е запис
        const opens =
          event.key === "Enter" ||
          event.key === " " ||
          event.key === "F2" ||
          (event.altKey && event.key === "ArrowDown");
        if (!opens) return;
        // preventDefault: решетката вижда клавиша като обработен (Enter тук
        // не слиза надолу, Alt+↓ не мести реда)
        event.preventDefault();
        setEditing(true);
      }}
      className={className}
    >
      {children}
    </div>
  );
};

export default PickCell;
